import axios from "axios";
import { encodeData } from "../common/encodeData";
import { getRequestedCountForBulk } from "./bulkPlacementBidUpdate";

export { getRequestedCountForBulk };

export const PRODUCT_ANALYTICS_CUBE = {
  ASIN: "VariantAnalysisTablePPMPlaceholder.asin",
  CAMPAIGN_ID: "VariantAnalysisTablePPMPlaceholder.campaignId",
  CAMPAIGN_NAME: "VariantAnalysisTablePPMPlaceholder.campaignName",
  AD_GROUP_ID: "VariantAnalysisTablePPMPlaceholder.adGroupId",
  AD_GROUP_NAME: "VariantAnalysisTablePPMPlaceholder.adGroupName",
  PORTFOLIO_ID: "VariantAnalysisTablePPMPlaceholder.portfolioId",
  PORTFOLIO_NAME: "VariantAnalysisTablePPMPlaceholder.portfolioName",
  AD_STATUS_ANY: "VariantAnalysisTablePPMPlaceholder.adStatusAnyEnabled",
  SKU: "VariantAnalysisTablePPMPlaceholder.sku",
};

export const PRODUCT_ANALYTICS_GRAIN = {
  INVALID: "INVALID",
  ASIN_ONLY: "ASIN_ONLY",
  CAMPAIGN: "CAMPAIGN",
  AD_GROUP: "AD_GROUP",
  PORTFOLIO: "PORTFOLIO",
};

export const POPULATE_LEVEL = {
  RESPECTIVE: "respective",
  CAMPAIGN: "campaign",
  ADGROUP: "adgroup",
};

function isSponsoredBrandObjective(objective) {
  return String(objective || "").toUpperCase().includes("SB");
}

function mapCampaignOption(item) {
  return {
    id: item?.id,
    name: item?.name || "",
    platform_id: item?.platform_id != null ? String(item.platform_id) : "",
    amz_portfolio_id:
      item?.amz_portfolio_id != null ? String(item.amz_portfolio_id) : "",
    objective: item?.objective || "",
    status: item?.status,
    label: item?.name || "",
    value: item?.id != null ? String(item.id) : "",
  };
}

function mapAdgroupOption(item) {
  return {
    id: item?.id,
    name: item?.name || "",
    platform_id: item?.platform_id != null ? String(item.platform_id) : "",
    campaign_id: item?.campaign_id != null ? String(item.campaign_id) : "",
    status: item?.status,
    label: item?.name || "",
    value: item?.id != null ? String(item.id) : "",
  };
}

export async function fetchPortfoliosForPopulateModal({
  clientId,
  searchTerm = "",
  limit = 200,
}) {
  const params = [
    "source=amazon",
    "fields=id,name,platform_id",
    `limit=${limit}`,
  ];
  const term = String(searchTerm || "").trim();
  if (term) {
    params.push(
      `filtering=${encodeData([
        { field_name: "name", operator: "CONTAINS", value: term },
      ])}`
    );
  }
  const url = `${process.env.REACT_APP_ADS_API}/client/${clientId}/portfolios?${params.join("&")}`;
  const response = await axios.get(url);
  return (response?.data?.data || [])
    .map((item) => ({
      id: item?.id,
      name: item?.name || "",
      platform_id: item?.platform_id != null ? String(item.platform_id) : "",
      label: item?.name || "",
      value:
        item?.platform_id != null
          ? String(item.platform_id)
          : String(item?.id || ""),
    }))
    .filter((item) => item.name);
}

export async function fetchCampaignsForPopulateModal({
  clientId,
  searchTerm = "",
  portfolioPlatformIds = [],
  limit = 200,
}) {
  const params = [
    "source=amazon",
    "fields=id,name,platform_id,status,amz_portfolio_id,objective",
    `limit=${limit}`,
    "order_by=daily_budget",
    "sorting_order=DESC",
  ];
  const filtering = [];
  const term = String(searchTerm || "").trim();
  if (term) {
    filtering.push({ field_name: "name", operator: "CONTAINS", value: term });
  }
  // Backend expects numeric ids for amz_portfolio_id (same as campaign-abo-create).
  const portfolioIds = [
    ...new Set(
      (portfolioPlatformIds || [])
        .map((id) => Number(id))
        .filter((n) => Number.isFinite(n))
    ),
  ];
  if (portfolioIds.length) {
    filtering.push({
      field_name: "amz_portfolio_id",
      operator: "IN",
      values: portfolioIds,
    });
  }
  if (filtering.length) {
    params.push(`filtering=${encodeData(filtering)}`);
  }
  const url = `${process.env.REACT_APP_ADS_API}/client/${clientId}/campaigns?${params.join("&")}`;
  const response = await axios.get(url);
  return (response?.data?.data || [])
    .map(mapCampaignOption)
    .filter((item) => item.id && !isSponsoredBrandObjective(item.objective))
    .filter((item) => {
      if (!portfolioIds.length) return true;
      return portfolioIds.includes(Number(item.amz_portfolio_id));
    });
}

export async function fetchAdgroupsForPopulateModal({
  clientId,
  campaignMongoIds = [],
  searchTerm = "",
  limit = 500,
}) {
  const params = [
    "source=amazon",
    "fields=id,name,platform_id,campaign_id,status",
    `limit=${limit}`,
  ];
  const filtering = [];
  const selectedCampaignIds = (campaignMongoIds || []).map(String).filter(Boolean);
  if (selectedCampaignIds.length) {
    filtering.push({
      field_name: "campaign_id",
      operator: "IN",
      values: selectedCampaignIds,
    });
  }
  const term = String(searchTerm || "").trim();
  if (term) {
    filtering.push({ field_name: "name", operator: "CONTAINS", value: term });
  }
  if (filtering.length) {
    params.push(`filtering=${encodeData(filtering)}`);
  }
  const url = `${process.env.REACT_APP_ADS_API}/client/${clientId}/adsets?${params.join("&")}`;
  const response = await axios.get(url);
  return (response?.data?.data || [])
    .map(mapAdgroupOption)
    .filter((item) => item.id);
}

export function hasCubeMember(dimensions = [], shortName = "") {
  const needle = String(shortName || "").toLowerCase();
  if (!needle) return false;
  return (dimensions || []).some((dimension) => {
    if (!dimension) return false;
    const last = String(dimension).includes(".")
      ? String(dimension).substring(String(dimension).lastIndexOf(".") + 1)
      : String(dimension);
    const normalized = last.toLowerCase();
    return (
      normalized === needle ||
      normalized === `${needle}measure` ||
      normalized === `${needle}dimension`
    );
  });
}

export function isChildAsinInGroupBy(dimensions = []) {
  return hasCubeMember(dimensions, "asin");
}

export function getProductAnalyticsGrain(dimensions = []) {
  if (!isChildAsinInGroupBy(dimensions)) {
    return PRODUCT_ANALYTICS_GRAIN.INVALID;
  }
  if (
    hasCubeMember(dimensions, "adGroupId") ||
    hasCubeMember(dimensions, "adGroupName")
  ) {
    return PRODUCT_ANALYTICS_GRAIN.AD_GROUP;
  }
  if (
    hasCubeMember(dimensions, "campaignId") ||
    hasCubeMember(dimensions, "campaignName")
  ) {
    return PRODUCT_ANALYTICS_GRAIN.CAMPAIGN;
  }
  if (
    hasCubeMember(dimensions, "portfolioId") ||
    hasCubeMember(dimensions, "portfolioName")
  ) {
    return PRODUCT_ANALYTICS_GRAIN.PORTFOLIO;
  }
  return PRODUCT_ANALYTICS_GRAIN.ASIN_ONLY;
}

export function getProductAnalyticsBulkIdKeys(dimensions = []) {
  const grain = getProductAnalyticsGrain(dimensions);
  switch (grain) {
    case PRODUCT_ANALYTICS_GRAIN.AD_GROUP:
      return [
        PRODUCT_ANALYTICS_CUBE.ASIN,
        PRODUCT_ANALYTICS_CUBE.CAMPAIGN_ID,
        PRODUCT_ANALYTICS_CUBE.AD_GROUP_ID,
      ];
    case PRODUCT_ANALYTICS_GRAIN.CAMPAIGN:
      return [PRODUCT_ANALYTICS_CUBE.ASIN, PRODUCT_ANALYTICS_CUBE.CAMPAIGN_ID];
    case PRODUCT_ANALYTICS_GRAIN.PORTFOLIO:
      return [PRODUCT_ANALYTICS_CUBE.ASIN, PRODUCT_ANALYTICS_CUBE.PORTFOLIO_ID];
    case PRODUCT_ANALYTICS_GRAIN.ASIN_ONLY:
      return [PRODUCT_ANALYTICS_CUBE.ASIN];
    default:
      return [];
  }
}

export function isAdStatusEnabled(row) {
  const value = row?.[PRODUCT_ANALYTICS_CUBE.AD_STATUS_ANY];
  return Number(value) > 0;
}

export function shouldCreateMissingByDefault(grain, status) {
  if (status !== "enabled") return false;
  return (
    grain === PRODUCT_ANALYTICS_GRAIN.CAMPAIGN ||
    grain === PRODUCT_ANALYTICS_GRAIN.AD_GROUP
  );
}

export function buildBulkAdStatusPatchBody({
  query,
  email,
  requestedCount,
  status,
  createMissing = false,
}) {
  return {
    filters: {
      query: { ...(query || {}) },
    },
    status,
    createMissing: Boolean(createMissing),
    email: email || "",
    requestedCount: Number(requestedCount) || 0,
  };
}

export async function submitBulkAdStatusUpdate({
  clientId,
  userPlatform = "amazon",
  skipEvents = true,
  body,
  token,
}) {
  const baseUrl = process.env.REACT_APP_ADS_API;
  const url = `${baseUrl}/client/${clientId}/ads:bulk-patch-update-ad?skipEvents=${
    skipEvents ? "true" : "false"
  }&source=${userPlatform}`;

  const headers = {
    "Content-Type": "application/json",
  };
  if (token) headers.Authorization = token;

  const response = await axios.patch(url, body, { headers });
  return response?.data;
}

export function buildBulkAdPopulateBody({
  query,
  level = POPULATE_LEVEL.ADGROUP,
  destinations = [],
  campaignIds = [],
  email,
  requestedCount,
}) {
  const body = {
    filters: {
      query: { ...(query || {}) },
    },
    level,
    email: email || "",
    requestedCount: Number(requestedCount) || 0,
  };
  if (level === POPULATE_LEVEL.CAMPAIGN) {
    body.campaign_ids = (campaignIds || []).map(String).filter(Boolean);
  }
  if (level === POPULATE_LEVEL.ADGROUP) {
    body.destinations = (destinations || []).map((destination) => ({
      campaign_id: destination.campaign_id || destination.campaignId,
      adset_id: destination.adset_id || destination.adsetId || destination.id,
    }));
  }
  return body;
}

export async function submitBulkAdPopulate({
  clientId,
  userPlatform = "amazon",
  skipEvents = true,
  body,
  token,
}) {
  const baseUrl = process.env.REACT_APP_ADS_API;
  const url = `${baseUrl}/client/${clientId}/ads:bulk-populate-ad?skipEvents=${
    skipEvents ? "true" : "false"
  }&source=${userPlatform}`;

  const headers = {
    "Content-Type": "application/json",
  };
  if (token) headers.Authorization = token;

  const response = await axios.post(url, body, { headers });
  return response?.data;
}
